import { useRef, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useCurrentTool } from '@/shared/hooks/useCurrentTool'
import { readAudioDuration } from '@/shared/audio/mediaDuration'

export const useFileUpload = (onFileSelected, disabled, confirmIfSilent) => {
  const { t } = useTranslation();
  const { nonAudioTool } = useCurrentTool();
  const fileInputRef = useRef(null);
  const [uploadModelOpen, setUploadModelOpen] = useState(false);
  const MAX_FILES = 5;

  useEffect(() => {
    const fileInput = fileInputRef.current;
    if (!fileInput) return;
    
    const handleFocus = () => {
      if (fileInput.value.length === 0) {
        setUploadModelOpen(false);
      }
    };
    
    const initialize = () => {
      window.addEventListener('focus', handleFocus, { once: true });
    };
    
    fileInput.addEventListener('click', initialize);
    
    return () => {
      fileInput.removeEventListener('click', initialize);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const validateFile = (file) => {
    const allowedAudioTypes = [
      'audio/flac',
      'audio/mp4',
      'video/mp4',
      'audio/mpeg',
      'audio/ogg',
      'audio/wav',
      'video/webm',
      'audio/webm',
      'audio/x-m4a',
      'audio/x-wav',
    ];

    const allowedDocTypes = [
      'application/pdf',
    ];

    if (file.size / (1024 * 1024) > 250) {
      toast.error('The file is too large. The maximum size is 250 MB.');
      return false;
    }

    const allowedTypes = nonAudioTool ? allowedDocTypes : allowedAudioTypes;
    if (!allowedTypes.includes(file.type)) {
      toast.error(`Invalid file type. Please upload ${nonAudioTool ? 'a PDF' : 'an audio'} file.`);
      return false;
    }

    return true;
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const acceptFile = async (file, durationSeconds) => {
    if (confirmIfSilent) {
      const proceed = await confirmIfSilent(file);
      if (!proceed) {
        resetFileInput();
        return;
      }
    }
    onFileSelected(file, durationSeconds);
  };

  const processFile = async (file) => {
    if (!validateFile(file)) {
      return;
    }

    if (!nonAudioTool && (file.type.startsWith('audio/') || file.type.startsWith('video/'))) {
      const durationSeconds = await readAudioDuration(file);
      await acceptFile(file, durationSeconds);
      return;
    }

    onFileSelected(file, 0);
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    
    if (nonAudioTool) {
      // For non-audio tools (PDF), handle multiple files up to MAX_FILES
      if (files.length > MAX_FILES) {
        toast.error(t('errors.maxFilesError', { maxFiles: MAX_FILES }));
        return;
      }
      
      const validFiles = [];
      for (const file of files) {
        if (validateFile(file)) {
          validFiles.push(file);
        }
      }
      
      if (validFiles.length > 0) {
        setUploadModelOpen(false);
        onFileSelected(validFiles, 0);
      }
    } else {
      // For audio tools, handle single file
      const file = files[0];
      if (file) {
        setUploadModelOpen(false);
        processFile(file);
      }
    }
  };

  const handleDrop = (event) => {
    if (disabled) return;
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    
    if (nonAudioTool) {
      // For non-audio tools (PDF), handle multiple files up to MAX_FILES
      if (files.length > MAX_FILES) {
        toast.error(t('errors.maxFilesError', { maxFiles: MAX_FILES }));
        return;
      }
      
      const validFiles = [];
      for (const file of files) {
        if (validateFile(file)) {
          validFiles.push(file);
        }
      }
      
      if (validFiles.length > 0) {
        onFileSelected(validFiles, 0);
      }
    } else {
      // For audio tools, handle single file
      const file = files[0];
      if (file) {
        processFile(file);
      }
    }
  };

  const handleDragOver = (event) => {
    if (disabled) return;
    event.preventDefault();
  };

  const handleBrowseClick = () => {
    if (!disabled && fileInputRef.current) {
      setUploadModelOpen(true);
      fileInputRef.current.click();
    }
  };

  return {
    fileInputRef,
    uploadModelOpen,
    handleFileChange,
    handleDrop,
    handleDragOver,
    handleBrowseClick,
    acceptedTypes: nonAudioTool ? 'application/pdf' : 'audio/*, video/mp4, video/webm',
    maxFiles: nonAudioTool ? MAX_FILES : 1
  };
};